import { Permission } from '@idc-forms/permissions'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { Loader2, MessageSquare, Reply, Send, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { cn } from '@/shared/lib/utils'
import { Avatar, AvatarFallback } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'
import { Textarea } from '@/shared/ui/textarea'
import { handleMutationError } from '@/shared/utils/handle-api-error'
import {
  useCreateInventoryComment,
  useDeleteInventoryComment,
  useInventoryComments
} from '../../hooks/use-inventory-comments'
import type { InventoryCommentView } from '../../models/inventory-comment-view'

interface CommentComposerProps {
  placeholder: string
  submitting: boolean
  onSubmit: (body: string) => void
  onCancel?: () => void
  autoFocus?: boolean
}

function CommentComposer({
  placeholder,
  submitting,
  onSubmit,
  onCancel,
  autoFocus
}: CommentComposerProps) {
  const [body, setBody] = useState('')

  const handleSubmit = () => {
    const trimmed = body.trim()
    if (!trimmed) return
    onSubmit(trimmed)
    setBody('')
  }

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        value={body}
        autoFocus={autoFocus}
        onChange={(e) => {
          setBody(e.target.value)
        }}
        placeholder={placeholder}
        rows={3}
        className="resize-none text-sm"
      />
      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          onClick={handleSubmit}
          disabled={submitting || body.trim().length === 0}
        >
          {submitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          Publicar
        </Button>
      </div>
    </div>
  )
}

interface CommentItemProps {
  comment: InventoryCommentView
  canReply: boolean
  canModerate: boolean
  isReply?: boolean
  onReply: (parentId: string, body: string) => void
  onDelete: (commentId: string) => void
  replyingSubmitting: boolean
  deletingId: string | null
}

function CommentItem({
  comment,
  canReply,
  canModerate,
  isReply = false,
  onReply,
  onDelete,
  replyingSubmitting,
  deletingId
}: CommentItemProps) {
  const [showReply, setShowReply] = useState(false)
  const canDelete = comment.isOwn || canModerate

  const relativeTime = formatDistanceToNow(parseISO(comment.createdAt), {
    addSuffix: true,
    locale: es
  })

  return (
    <div className={cn('flex flex-col gap-2', isReply && 'pl-5 border-l ml-3')}>
      <div className="flex gap-2">
        <Avatar seed={comment.authorSeed} className="h-7 w-7 shrink-0">
          <AvatarFallback className="text-xs">{comment.authorName[0] ?? '?'}</AvatarFallback>
        </Avatar>
        <div className="flex flex-col gap-0.5 w-full">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-accent-foreground">{comment.authorName}</span>
            <span className="text-xs text-muted-foreground">{relativeTime}</span>
          </div>
          <p className="text-sm text-neutral-700 dark:text-neutral-200 whitespace-pre-wrap break-words">
            {comment.body}
          </p>
          <div className="flex items-center gap-1">
            {canReply && !isReply && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground"
                onClick={() => {
                  setShowReply((v) => !v)
                }}
              >
                <Reply className="size-3.5" /> Responder
              </Button>
            )}
            {canDelete && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                onClick={() => {
                  onDelete(comment.id)
                }}
                disabled={deletingId === comment.id}
              >
                {deletingId === comment.id ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Trash2 className="size-3.5" />
                )}{' '}
                Eliminar
              </Button>
            )}
          </div>

          {showReply && (
            <div className="mt-2">
              <CommentComposer
                placeholder="Escribe una respuesta…"
                submitting={replyingSubmitting}
                autoFocus
                onCancel={() => {
                  setShowReply(false)
                }}
                onSubmit={(body) => {
                  onReply(comment.id, body)
                  setShowReply(false)
                }}
              />
            </div>
          )}
        </div>
      </div>

      {comment.replies.length > 0 && (
        <div className="flex flex-col gap-3">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              canReply={canReply}
              canModerate={canModerate}
              isReply
              onReply={onReply}
              onDelete={onDelete}
              replyingSubmitting={replyingSubmitting}
              deletingId={deletingId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface InventoryCommentsProps {
  /** El `_id` de Mongo del inmueble. */
  propertyId: string
  /** Su `prp_`, cuando el payload lo trae: es lo que decide si esto habla v2. */
  propertyPublicId?: string
}

export default function InventoryComments({
  propertyId,
  propertyPublicId
}: InventoryCommentsProps) {
  const { has } = usePermissions()

  const canCreate = has(Permission.CreateInventoryComment)
  const canModerate = has(Permission.ManageInventoryComments)

  const source = { propertyId, propertyPublicId }
  const { data: comments = [], isLoading } = useInventoryComments(source)
  const createMutation = useCreateInventoryComment(source)
  const deleteMutation = useDeleteInventoryComment(source)

  const [deletingId, setDeletingId] = useState<string | null>(null)

  const handleCreate = (body: string, parent?: string) => {
    createMutation.mutate(
      { body, parent },
      {
        onError: (error) => {
          handleMutationError(error)
        }
      }
    )
  }

  const handleDelete = (commentId: string) => {
    setDeletingId(commentId)
    deleteMutation.mutate(commentId, {
      onError: (error) => {
        handleMutationError(error)
      },
      onSettled: () => {
        setDeletingId(null)
      }
    })
  }

  return (
    <div className="flex flex-col gap-4 py-3 pl-3 pr-2">
      {canCreate && (
        <CommentComposer
          placeholder="Escribe una nota u observación…"
          submitting={createMutation.isPending}
          onSubmit={(body) => {
            handleCreate(body)
          }}
        />
      )}

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-2">
              <Skeleton className="h-7 w-7 rounded-full" />
              <div className="flex flex-col gap-1 w-full">
                <Skeleton className="h-3 w-1/3" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : comments.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="default">
              <MessageSquare className="h-16 w-16" />
            </EmptyMedia>
            <EmptyTitle>Sin comentarios</EmptyTitle>
            <EmptyDescription>
              {canCreate
                ? 'Deja la primera nota u observación sobre este inmueble.'
                : 'Aún no hay comentarios para este inmueble.'}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="flex flex-col gap-4">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              canReply={canCreate}
              canModerate={canModerate}
              onReply={(parentId, body) => {
                handleCreate(body, parentId)
              }}
              onDelete={handleDelete}
              replyingSubmitting={createMutation.isPending}
              deletingId={deletingId}
            />
          ))}
        </div>
      )}
    </div>
  )
}
